import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatFilterPrice } from '@investoro/core';
import { UserActions } from 'store/User/Actions';
import { hasValidSubscription, useUser } from 'store/User/Context';
import styled from 'styled-components';
import { SubscriptionPurchaseContext } from 'utils/context/SubscriptionPurchaseContext';

import Button from 'components/atom/Button';
import Indicator from 'components/atom/Indicator';
import { IconEnum } from 'components/common/Icon';
import ProductPurchaseForm from 'components/common/Purchase/ProductPurchaseForm';
import PurchaseStatusInfo, { PurchaseProductEnum } from 'components/common/Purchase/PurchaseStatusInfo';
import { activeNotification } from 'components/functions/activeNotification';

const PageContainer = styled.div<{ isBackgroundTransparent?: boolean }>`
    display: flex;
    width: 98%;
    height: auto;
    padding: 20px;
    margin: 20px;
    border-radius: 10px;
    background: ${(props) => props.isBackgroundTransparent ? 'transparent' : 'var(--color-white)'};
    
    @media (max-width: 992px) {
        flex-direction: column;
        padding: 10px;
        margin: 10px;
    }
`;

const FormSection = styled.div`
    width: 66%;
    padding-right: 20px;
    
    @media (max-width: 992px) {
        width: 100%;
        padding-right: 0;
    }
`;

const OrderSummarySection = styled.div`
    width: 33%;
    border-left: 1px solid var(--color-alt-second-10);
    padding-left: 20px;
    padding-top: 130px;

    @media (max-width: 992px) {
        width: 100%;
        margin-top: 30px;
        border-left: none;
        padding-left: 0;
    }
`;

const OrderSummaryCard = styled.div`
    padding: 25px;
`;

const OrderSummaryTitle = styled.h3`
    font-size: 18px;
    margin-bottom: 20px;
    color: var(--color-primary);
    font-weight: 600;
`;

const OrderSummaryItem = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 15px;
    font-size: 14px;
`;

const OrderSummaryTotal = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid #eee;
    font-weight: 600;
    font-size: 16px;
`;

const ButtonContainer = styled.div`
    width: 100%;
    margin-top: 30px;
    display: flex;
    flex-direction: column;
    gap: 10px;

    @media (max-width: 768px) {
        margin-top: 20px;
    }
`;

const StyledButton = styled(Button)`
    width: 100%;
    max-width: 400px;
`;

const LoadingOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
`;

const LoadingContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
`;

const LoadingText = styled.div`
    padding-bottom: 100px;
    font-size: 16px;
    color: var(--color-primary);
    text-align: center;
    z-index: 999999;
`;

const CreateAccountAndPurchaseAppPage = () => {
    const { user, dispatch } = useUser();
    const navigate = useNavigate();
    const {
        subscriptionState,
        handleAccountDataChange,
        handleInvoiceDataChange,
        handleSubmitForm,
        handleCancel
    } = useContext(SubscriptionPurchaseContext);

    const isLoggedUser = user.isLogged;

    const handleCheckPaymentStatus = async () => {
        try {
            const refreshSuccess = await UserActions.refreshUserToken(dispatch);

            if (refreshSuccess) {
                if (hasValidSubscription(user.userRole) || user.activePackages?.length > 0) {
                    activeNotification(
                        'Płatność potwierdzona!',
                        'Twoja subskrypcja została aktywowana',
                        'success'
                    );

                    navigate('/');
                } else {
                    activeNotification(
                        'Status płatności',
                        'Płatność nadal nie została potwierdzona. Spróbuj ponownie za chwilę.',
                        'warning'
                    );
                }
            } else {
                activeNotification(
                    'Błąd',
                    'Nie udało się sprawdzić statusu płatności',
                    'danger'
                );
            }
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
            activeNotification(
                'Błąd',
                'Nie udało się sprawdzić statusu płatności',
                'danger'
            );
        }
    };

    return (
        <PageContainer isBackgroundTransparent={subscriptionState.showPurchaseStatus}>
            {subscriptionState.showPurchaseStatus
                ? <PurchaseStatusInfo
                    key={`${subscriptionState.status}-${subscriptionState.processStep}-${subscriptionState.isLoading}`}
                    purchaseType={PurchaseProductEnum.SUBSCRIPTION}
                    status={subscriptionState.status}
                    processStep={subscriptionState.processStep}
                    orderData={subscriptionState.currentOrder ? subscriptionState.currentOrder : undefined}
                    isNewAccount={!isLoggedUser}
                    isLoading={subscriptionState.isLoading}
                    isPolling={subscriptionState.isPolling}
                    onCancel={handleCancel}
                    onCheckStatus={handleCheckPaymentStatus}
                    error={subscriptionState.error}
                />

                : <>
                    <FormSection>
                        <ProductPurchaseForm
                            isCreateAccount={!isLoggedUser}
                            isDarkMode={user.isDarkMode}
                            accountData={subscriptionState.accountData}
                            invoiceData={subscriptionState.invoiceData}
                            errors={subscriptionState.errors}
                            triggerValidation={subscriptionState.triggerValidation}
                            onAccountDataChange={handleAccountDataChange}
                            onInvoiceDataChange={handleInvoiceDataChange}
                            onSubmit={handleSubmitForm}
                        />
                    </FormSection>

                    <OrderSummarySection>
                        <OrderSummaryCard>
                            <OrderSummaryTitle>Podsumowanie zamówienia</OrderSummaryTitle>

                            <OrderSummaryItem>
                                <div>
                                    <strong>{subscriptionState.subscriptionProduct?.name}</strong>
                                    <br />
                                    <small style={{ color: 'var(--color-text-secondary)' }}>
                                        {subscriptionState.subscriptionProduct?.description}
                                    </small>
                                </div>
                                <div>
                                    <strong>{formatFilterPrice(subscriptionState?.subscriptionProduct?.price)} zł</strong>
                                </div>
                            </OrderSummaryItem>

                            <OrderSummaryTotal>
                                <span>Razem do zapłaty:</span>
                                <span>{formatFilterPrice(subscriptionState?.subscriptionProduct?.price)} zł</span>
                            </OrderSummaryTotal>
                        </OrderSummaryCard>

                        <ButtonContainer>
                            <StyledButton
                                onClick={handleSubmitForm}
                                actionIcon={IconEnum.SEND}
                                disabled={subscriptionState.isLoading || subscriptionState.isPolling}
                            >
                                {subscriptionState.isLoading
                                    ? 'Przetwarzanie...'
                                    : subscriptionState.isPolling
                                        ? 'Oczekiwanie na płatność...'
                                        : isLoggedUser ? 'wykup subskrypcję' : 'Załóż konto i wykup subskrypcję'}
                            </StyledButton>

                            <StyledButton
                                onClick={handleCancel}
                                actionIcon={IconEnum.CROSS}
                                disabled={subscriptionState.isLoading && !subscriptionState.isPolling}
                                important
                            >
                                Anuluj
                            </StyledButton>
                        </ButtonContainer>
                    </OrderSummarySection>

                    {(subscriptionState.isLoading || subscriptionState.isPolling) &&
                        <LoadingOverlay>
                            <LoadingContent>
                                <Indicator />
                                {subscriptionState.processStep && <LoadingText>{subscriptionState.processStep}</LoadingText>}
                            </LoadingContent>
                        </LoadingOverlay>
                    }
                </>
            }
        </PageContainer>
    );
};

export default CreateAccountAndPurchaseAppPage;
